import { ServiceIDs } from "@/utils/constants";
import { getRoamingDataGb } from "@/utils/productMatrix";
import {
  combineProductsByBaselabel,
  Duration,
  getFlatHomeBaseDisplayValue,
  isAnyFlatMobileTariff,
  isFlatHomeTariff,
  isFlatMobileTariff,
  TransformedProduct,
  TransformedProductsById,
} from "@/utils/tariffUtils";

export const getMobileAbos = (
  processedProductsById: TransformedProductsById | undefined,
) =>
  processedProductsById
    ? Object.values(processedProductsById || {}).filter((product) =>
        isAnyFlatMobileTariff(product.id),
      )
    : [];

export const getSortedMobileAbosMonthly = (
  mobileAbos: TransformedProduct[],
) => {
  // Smallest plan first (One, Two, Three), by the included roaming volume in
  // the product matrix.
  const tierRank = (abo: TransformedProduct) =>
    getRoamingDataGb(abo.id) ?? Number.MAX_SAFE_INTEGER;

  return mobileAbos
    .filter((abo) => abo.duration === Duration.month)
    .sort((a, b) => tierRank(a) - tierRank(b));
};

/** Data-only tariffs (excludes Flat Mobile voice abos and Flat Home). */
export const getDataOnlyProducts = (
  processedProductsById: TransformedProductsById | undefined,
) =>
  Object.entries(combineProductsByBaselabel(processedProductsById ?? {}))
    .filter(
      ([rate, [monthOption]]) =>
        !isFlatMobileTariff(rate) && !isFlatHomeTariff(monthOption?.id),
    )
    .map(([, [monthOption]]) => monthOption)
    .filter(Boolean);

const flatHomeProductOrder = [ServiceIDs.FlatHome4G, ServiceIDs.FlatHome5G];

/** Flat Home 4G/5G monthly options, sorted for display. */
export const getFlatHomeProducts = (
  processedProductsById: TransformedProductsById | undefined,
) =>
  Object.entries(combineProductsByBaselabel(processedProductsById ?? {}))
    .map(([, [monthOption]]) => monthOption)
    .filter(
      (product): product is TransformedProduct =>
        !!product && isFlatHomeTariff(product.id),
    )
    .sort(
      (a, b) =>
        flatHomeProductOrder.indexOf(a.id!) -
        flatHomeProductOrder.indexOf(b.id!),
    );

const flatHomeFallbackProduct = (
  id: (typeof flatHomeProductOrder)[number],
): TransformedProduct => ({
  id,
  baseDisplayValue: getFlatHomeBaseDisplayValue(id),
  chargeFlat: "",
  isVoiceProduct: false,
  data: id,
  voice: id,
});

/**
 * Flat Home options for partner flows. The smartphone options endpoint (131001)
 * often omits Flat Home; fall back to catalogue ids so the tariff stays selectable.
 */
export const getFlatHomeProductsWithFallback = (
  processedProductsById: TransformedProductsById | undefined,
) => {
  const fromApi = getFlatHomeProducts(processedProductsById);

  if (fromApi.length > 0) {
    return fromApi;
  }

  return flatHomeProductOrder.map(flatHomeFallbackProduct);
};
